import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Clock, ShieldCheck, Wrench } from "lucide-react";

const CATEGORY_LABELS = {
  heating: "Chauffage",
  pump: "Pompe",
  electronics: "Électronique",
  water: "Traitement eau",
  filter: "Filtration",
  other: "Autres",
};

const DIFF_STYLES = {
  easy: "bg-emerald-100 text-emerald-800",
  medium: "bg-amber-100 text-amber-800",
  hard: "bg-red-100 text-red-800",
};

/**
 * Suggested repair kits block — shown on model detail pages to convert
 * consulters into buyers.
 * Props:
 * - brand?: string (spa brand e.g. "Balboa")
 * - category?: string (kit category slug e.g. "electronics")
 * - limit?: number (default 3)
 */
export default function SuggestedKits({ brand, category, limit = 3, title, subtitle }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    setLoading(true);
    api
      .get("/kits/suggested", { params: { brand: brand || undefined, category: category || undefined, limit } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [brand, category, limit]);

  if (loading || items.length === 0) return null;

  return (
    <section className="mt-14" data-testid="suggested-kits">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-secondary">
            <ShieldCheck className="h-3 w-3" /> Boutique DoctoSpa
          </div>
          <h3 className="mt-2 font-display text-2xl lg:text-3xl font-black tracking-tight">
            {title || `Kits ${brand ? `${brand} ` : ""}prêts à monter`}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {subtitle || "Toutes les pièces sélectionnées, testées et livrées dans un même carton — avec notice."}
          </p>
        </div>
        <Link to="/kits" className="text-sm text-primary hover:underline font-semibold" data-testid="link-all-kits">
          Voir tous les kits →
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((k) => (
          <Link
            key={k.id}
            to={`/kits/${k.slug}`}
            className="group"
            data-testid={`suggested-kit-${k.slug}`}
          >
            <Card className="h-full border-black/10 overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all flex flex-col">
              <div className="aspect-[16/10] bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 grid place-items-center overflow-hidden">
                {k.cover_photo_url ? (
                  <img
                    src={`${backend}${k.cover_photo_url}`}
                    alt={k.name}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <Package className="h-12 w-12 text-white/40" strokeWidth={1.2} />
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-1 text-[10px]">
                  <Badge variant="secondary" className="rounded-full">{CATEGORY_LABELS[k.category] || k.category}</Badge>
                  <Badge className={`rounded-full border-0 ${DIFF_STYLES[k.difficulty] || DIFF_STYLES.medium}`}>
                    {k.difficulty}
                  </Badge>
                </div>
                <h4 className="mt-2 font-display font-bold text-sm leading-tight group-hover:text-primary transition-colors line-clamp-2">
                  {k.name}
                </h4>
                <div className="mt-3 flex items-end justify-between">
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {k.estimated_time_min} min
                  </span>
                  <span className="font-display text-lg font-black text-primary">
                    {k.price_display}
                  </span>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
